import React, { useState } from 'react';
import Styled from './styled';
import { Button, ButtonProps } from '../index';
import { RatingStar } from '../Icon';

export interface SelectProps {
    /** Alignment of the select items, defaults to 'left'. */
    alignment?: 'left' | 'center' | 'right';
    /** Orientation of the select items, defaults to 'vertical'. */
    orientation?: 'vertical' | 'horizontal';
    /** Optional header text for the select component. */
    header?: string;
    /** List of button items for the select component. */
    items?: ButtonProps[];
    /** Whether feedback flag is enabled, defaults to false. */
    feedback?: boolean;
    /** Whether rating flag is enabled, defaults to false. */
    rating?: boolean;
    /** Number of rating stars only applicable if 'rating' is true, defaults to 5. */
    count?: number;
    /** Click handler for rating items, Defaults to passing rating value */
    onRatingClick?: (value: number) => void;
    /** Whether the select items are disabled. */
    disabled?: boolean;
    /** Optional, selected option (rating / item)*/
    selectedOption?: string | number | null;
    /** Optional, stretch select options (buttons) to occupy full width (defaults to false)*/
    stretch?: boolean;
};

/**
 * Select component that renders a list of buttons or rating stars based on the provided props.
 * 
 * @param {SelectProps} props - The properties to configure the Select component.
 * @returns {JSX.Element} The rendered Select component.
 */
export const Select: React.FC<SelectProps> = ({
    alignment = 'left',
    orientation = 'vertical',
    items,
    feedback = false,
    rating = false,
    count = 5,
    onRatingClick,
    disabled = false,
    header = '',
    selectedOption = null,
    stretch = false,
}) => {
    /**
     * Renders the buttons or rating stars based on the provided props.
     * 
     * @returns {JSX.Element[]} The rendered buttons or rating stars.
     */
    const [selected, setSelected] = useState<string | number | null>(selectedOption);

    const handleOptionClick = (value: string) => {
        setSelected(value);
    };

    const handleRatingClick = (index: number) => {
        const rating = index + 1;
        setSelected(rating);
        onRatingClick?.(rating);
    };

    const renderButtons = () => {
        if (rating) {
            return [...Array(count)].map((_, index) => (
                <Styled.RatingButton
                    key={index}
                    onClick={() => handleRatingClick(index)}
                    disabled={disabled || selected !== null}
                    selected={index < (selected as number || 0)}
                >
                    <RatingStar />
                </Styled.RatingButton>
            ));
        }

        if (items) {
            return items.map((item, index) => (
                <Button
                    key={index}
                    {...item}
                    onClick={() => {
                        if (!item.disabled && !disabled) {
                            handleOptionClick(item.value);
                            item.onClick && item.onClick(item.value);
                        }
                    }}
                    disabled={item.disabled || disabled || selected !== null}
                    selected={item.selected ? item.selected : selected === item.value}
                    stretch={stretch}
                />
            ));
        }
    };

    return (
        <Styled.SelectWrapper>
            {header && <Styled.HeaderText>{header}</Styled.HeaderText>}
            <Styled.SelectContainer alignment={alignment}>
                {feedback && <Styled.Indentation className='select-indentation'/>}
                <Styled.SelectItems
                    alignment={alignment}
                    orientation={orientation}
                    feedback={feedback}
                >
                    {renderButtons()}
                </Styled.SelectItems>
            </Styled.SelectContainer>
        </Styled.SelectWrapper>
    );
};
